import { createBrowserRouter, Navigate } from 'react-router-dom'

import { AppShell } from '@/components/layout/AppShell'
import { RequireSession } from '@/features/auth/components/RequireSession'
import { CaseDetailPage } from '@/routes/CaseDetailPage'
import { CaseInvoicePage } from '@/routes/CaseInvoicePage'
import { CaseLegPage } from '@/routes/CaseLegPage'
import { CasesPage } from '@/routes/CasesPage'
import { ChangePasswordPage } from '@/routes/ChangePasswordPage'
import { ChatPage } from '@/routes/ChatPage'
import { ClientPage } from '@/routes/ClientPage'
import { ClientsPage } from '@/routes/ClientsPage'
import { CounterpartiesPage } from '@/routes/CounterpartiesPage'
import { CounterpartyPage } from '@/routes/CounterpartyPage'
import { DirectoriesPage } from '@/routes/DirectoriesPage'
import { FinancePage } from '@/routes/FinancePage'
import { GlossaryPage } from '@/routes/GlossaryPage'
import { HomeRoute } from '@/routes/HomeRoute'
import { LoginPage } from '@/routes/LoginPage'
import { MyTasksPage } from '@/routes/MyTasksPage'
import { NotFoundPage } from '@/routes/NotFoundPage'
import { ProfilePage } from '@/routes/ProfilePage'
import { QuoteCreatePage } from '@/routes/QuoteCreatePage'
import { QuoteDetailPage } from '@/routes/QuoteDetailPage'
import { QuotesPage } from '@/routes/QuotesPage'
import { SettingsPage } from '@/routes/SettingsPage'

/* Routes mirror the product's information architecture. Role filtering
 * lives in lib/navigation and shapes what is RENDERED; it is not access
 * control. The API is the only gate (AD-21), so a user who types a URL they may
 * not read gets a 403 from the server and the page shows it plainly.
 *
 * Sections WITHIN a record — the case rail, the finance tabs, the directory
 * tabs — are search params rather than nested routes. They stay linkable and
 * survive a refresh either way, but a search param also keeps the surrounding
 * chrome mounted across a switch: a case's sticky header and its chat panel do
 * not remount, so a half-typed message and the reader's scroll position
 * survive a trip to the Legs section. */

export const router = createBrowserRouter([
  /* Sign-in sits OUTSIDE the guard and outside the shell (Story 1.2). It has
   * no navigation, no topbar and no session, so wrapping it in either would be
   * a frame around a page that has nothing to frame — and RequireSession would
   * bounce a signed-out user straight back to it. */
  { path: '/login', element: <LoginPage /> },

  {
    path: '/',
    /* Everything below is behind a session. The guard is a REDIRECT, not access
     * control — the API is the only gate (AD-21) — so its job is to spare a
     * signed-out user a screenful of 401s and send them somewhere they can act. */
    element: <RequireSession />,
    children: [
      /* The password change (QW-5), under the guard because it needs the
       * session, and BESIDE the shell rather than inside it: a flagged account
       * is refused every route but the session read and this change, so the
       * shell's reads, the notification stream and the chat socket would each
       * be a 403. RequireSession sends a flagged account here and lets this
       * one path through. An unflagged reader may open it from Profile. */
      { path: 'change-password', element: <ChangePasswordPage /> },

      {
        element: <AppShell />,
        children: [
          /* Resolved by capability rather than fixed: the dashboard is the
             home of whoever may read it, and everyone else starts on «Мои
             задачи» (S-2b.1; Story 1.4, UX-DR-29). */
          { index: true, element: <HomeRoute /> },

          /* «Мои задачи»: the quotes and cases waiting on this reader, composed
             per reader by the server (GET /api/my-tasks). */
          { path: 'tasks', element: <MyTasksPage /> },

          { path: 'quotes', element: <QuotesPage /> },

          /* Creation is a PAGE, not a modal (UJ-1): the server mints the
             number, and nothing — cargo, costing, routings — can hang off a
             record that does not exist yet. So there is one screen for the
             questions the create payload carries, and everything after it
             happens on the record itself at /quotes/:id.

             There is no /quotes/:id/build any more. The three-step builder that
             lived there hosted the same cargo table, cost editor, route options
             and terms cards as the detail page, which gave the product two
             answers to every question and one flow that was reachable for a
             single visit.

             'new' sits ABOVE ':quoteId' because the router matches in order and
             would otherwise read the literal as an id and fetch quote "new". */
          { path: 'quotes/new', element: <QuoteCreatePage /> },

          { path: 'quotes/:quoteId', element: <QuoteDetailPage /> },

          { path: 'cases', element: <CasesPage /> },
          // Tabs are ?tab=cases|quotes, not nested routes, so the thread
          // beside the list stays mounted across a switch.
          { path: 'chat', element: <ChatPage /> },
          { path: 'cases/:caseId', element: <CaseDetailPage /> },

          /* One invoice, on its own URL, and NOT a `?section=` on the case.
             The two devices are not interchangeable here: a section param keeps
             the case's chrome mounted around a view of the same record, and an
             invoice is a different record with its own reads, its own writes and
             its own irreversible act. It is also the one thing on a case a
             colleague is sent directly — "look at AB-2026-0184", not "open the
             case and find it" — which a param cannot express without dragging
             the case's whole rail along behind it.

             Below `cases/:caseId` rather than above, because the two cannot
             collide: this path has three more segments than that one, so no
             literal here can ever be read as a case id. */
          { path: 'cases/:caseId/invoices/:invoiceId', element: <CaseInvoicePage /> },

          /* One direction, on its own URL, for the invoice route's reason above
             and one of its own: a direction carries a ROUTE CHAIN, and a chain
             is read across rather than down a 440px column. «Посмотри
             направление 2» is the sentence Operations says, and a `?section=`
             cannot express it without dragging the case's whole rail along.

             Three segments deeper than `cases/:caseId`, so no literal here can
             ever be read as a case id.

             ONE route, and `legs/new` is this same path with the literal in the
             param — the deliberate opposite of `quotes/new`, which sits above
             its own `:id` as a separate entry. The create and the record are the
             same surface at two stages here, and splitting them into two route
             elements is what forced the old create page to arm its own copy of
             the template-apply mutation: `navigate(replace)` then swapped one
             element for another, unmounting the surface holding the refusal
             before it could print. As one route it is a PARAM change, the page
             stays mounted, and a hundred lines of half-landed-create recovery
             stopped being necessary. `CaseLegPage` dispatches on the literal;
             nothing fetches a leg called "new". */
          { path: 'cases/:caseId/legs/:legId', element: <CaseLegPage /> },

          { path: 'finance', element: <FinancePage /> },

          /* Clients: the overview, and one client's page under it.
           * A section of its own rather than a directory tab, because a
           * client has money and a statement and the directory tabs have
           * neither. */
          { path: 'clients', element: <ClientsPage /> },
          { path: 'clients/:clientId', element: <ClientPage /> },
          /* Counterparties (S-22b): every company or person that provides us a
           * service, one record with a category — the directory's «Участники»
           * tab became this section, and `/directories?tab=participants`
           * lands here. */
          { path: 'counterparties', element: <CounterpartiesPage /> },
          { path: 'counterparties/:counterpartyId', element: <CounterpartyPage /> },
          { path: 'directories', element: <DirectoriesPage /> },
          { path: 'settings', element: <SettingsPage /> },

          /* Reached from the profile block in persistent chrome, not from the
           * nav tree — it is every user's own account rather than a section, so
           * it is deliberately absent from lib/navigation. */
          { path: 'profile', element: <ProfilePage /> },

          /* The interface glossary (QW-2), linked from the same footer as the
           * profile and absent from lib/navigation for the same reason: it is
           * every reader's, gates on nothing and reads nothing from the server. */
          { path: 'glossary', element: <GlossaryPage /> },

          { path: '404', element: <NotFoundPage /> },
          { path: '*', element: <Navigate to="/404" replace /> },
        ],
      },
    ],
  },
])
